import {
  type ChannelPingResult,
  type ChannelState,
  NOTIFICATION_CHANNELS,
  OPS_STATUS_LABELS,
  type OpsCheckView,
  type OpsStatus,
  type SystemStatus,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DeliveryList } from '../../components/NotificationsPanel';
import { api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { Button } from '../../ui/button';
import { Badge, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { KeyValue, Panel } from '../../ui/layout';

const KEY = ['admin', 'system-status'];
const TONE: Record<OpsStatus, 'success' | 'warning' | 'danger' | 'neutral'> = {
  ok: 'success',
  warn: 'warning',
  fail: 'danger',
  off: 'neutral',
};

const StatusBadge = ({ status }: { status: OpsStatus }) => (
  <Badge tone={TONE[status]}>{OPS_STATUS_LABELS[status]}</Badge>
);

function ChannelRow({ title, state, extra }: { title: string; state: ChannelState; extra?: string | null }) {
  return (
    <li className="flex flex-col gap-1 py-3 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium text-ink">{title}</span>
        <StatusBadge status={state.status} />
      </div>
      <div className="text-[13px] [overflow-wrap:anywhere] text-ink-2">
        {[extra, state.detail, state.checkedAt ? `проверено ${dateTime(state.checkedAt)}` : null]
          .filter(Boolean)
          .join(' · ')}
      </div>
    </li>
  );
}

function sizeText(bytes: number | null): string | null {
  if (bytes === null) return null;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
  if (bytes < 1024 ** 3) return `${Math.round(bytes / 1024 / 1024)} МБ`;
  return `${(bytes / 1024 ** 3).toFixed(1).replace('.', ',')} ГБ`;
}

function CheckList({ checks }: { checks: OpsCheckView[] }) {
  return (
    <ul className="flex flex-col divide-y divide-line">
      {checks.map((c) => (
        <li key={c.key} className="flex flex-col gap-1 py-3 first:pt-0 last:pb-0">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="font-medium text-ink">{c.title}</span>
            <StatusBadge status={c.status} />
          </div>
          <div className="text-[13px] [overflow-wrap:anywhere] text-ink-2">
            {[c.detail, sizeText(c.sizeBytes), c.lastOkAt ? `в порядке с ${dateTime(c.lastOkAt)}` : null]
              .filter(Boolean)
              .join(' · ')}
          </div>
        </li>
      ))}
    </ul>
  );
}

/**
 * Состояние системы (АД-06, УВ-09): работают ли почта и Telegram, дошли ли сообщения сотрудникам; владельцу
 * портала — ещё копии, проверка восстановления, реплика, очереди и события. Настройки каналов (SMTP, токен бота)
 * задаёт владелец на сервере, здесь они только проверяются.
 */
export function SystemStatusPage() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: KEY,
    queryFn: () => api<SystemStatus>('/admin/system-status'),
    refetchInterval: 60_000,
  });
  const recheck = useMutation({
    mutationFn: () => api<SystemStatus>('/admin/system-status/check', { method: 'POST' }),
    onSuccess: (data) => {
      qc.setQueryData(KEY, data);
      toast('Каналы проверены');
    },
  });
  const ping = useMutation({
    mutationFn: () => api<ChannelPingResult>('/admin/system-status/ping', { method: 'POST' }),
    onSuccess: (r) => {
      toast(
        r.results.length
          ? r.results
              .map(
                (x) =>
                  `${NOTIFICATION_CHANNELS[x.channel]}: ${x.status === 'sent' ? 'доставлено' : (x.error ?? 'ждёт повтора')}`,
              )
              .join('; ')
          : 'У вас не подключено ни одного канала: Профиль → Уведомления',
        r.results.some((x) => x.status !== 'sent') ? 'danger' : 'success',
      );
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });

  if (q.isPending) return <Spinner />;
  if (q.error) return <ErrorBox error={q.error} />;
  const s = q.data;
  const noChannels = s.channels.email.status === 'off' && s.channels.telegram.status === 'off';
  return (
    <div className="flex flex-col gap-6">
      <Panel
        title="Каналы уведомлений"
        meta="Почту и бота Telegram настраивает владелец портала на сервере"
        actions={
          <div className="flex flex-wrap gap-2">
            <Button size="sm" loading={recheck.isPending} onClick={() => recheck.mutate()}>
              Проверить сейчас
            </Button>
            <Button size="sm" loading={ping.isPending} disabled={noChannels} onClick={() => ping.mutate()}>
              Прислать пробное мне
            </Button>
          </div>
        }
      >
        {noChannels ? (
          <p className="mb-3 text-[14px] text-ink-2">
            Ни почта, ни Telegram не настроены: сводки и предупреждения о выкладке уходят только в портале.
            Владельцу портала нужно задать SMTP_URL, MAIL_FROM и TELEGRAM_BOT_TOKEN в настройках сервера.
          </p>
        ) : null}
        <ul className="flex flex-col divide-y divide-line">
          <ChannelRow title="Почта" state={s.channels.email} extra={s.channels.email.from} />
          <ChannelRow
            title="Бот Telegram"
            state={s.channels.telegram}
            extra={s.channels.telegram.botUsername ? `@${s.channels.telegram.botUsername}` : null}
          />
          <ChannelRow title="Webhook Telegram" state={s.channels.webhook} />
        </ul>
        {recheck.error ? <ErrorBox className="mt-3" error={recheck.error} /> : null}
        {ping.error ? <ErrorBox className="mt-3" error={ping.error} /> : null}
      </Panel>

      <Panel title="Доставка сотрудникам компании" meta="За последние сутки">
        <KeyValue
          items={[
            { label: 'Доставлено', value: s.delivery.sent },
            {
              label: 'Не доставлено',
              value: s.delivery.failed ? <Badge tone="danger">{s.delivery.failed}</Badge> : 0,
            },
            { label: 'Пропущено (нечего отправлять)', value: s.delivery.skipped },
          ]}
        />
        {s.delivery.recentFailed.length ? (
          <div className="mt-4">
            <h3 className="mb-2 text-[13px] font-medium text-ink-2">Недоставленное за 7 дней</h3>
            <DeliveryList items={s.delivery.recentFailed} withName />
          </div>
        ) : null}
      </Panel>

      <Panel title="Службы и защита соединения">
        <KeyValue
          items={[
            {
              label: 'База данных',
              value: (
                <Badge tone={s.ready.db ? 'success' : 'danger'}>
                  {s.ready.db ? 'отвечает' : 'не отвечает'}
                </Badge>
              ),
            },
            {
              label: 'Очередь задач (Redis)',
              value: (
                <Badge tone={s.ready.redis ? 'success' : 'warning'}>
                  {s.ready.redis ? 'отвечает' : 'не подключена'}
                </Badge>
              ),
            },
            {
              label: 'Хранилище файлов',
              value: (
                <Badge tone={s.ready.storage ? 'success' : 'warning'}>
                  {s.ready.storage ? 'отвечает' : 'не подключено'}
                </Badge>
              ),
            },
            {
              label: 'Адрес портала по HTTPS',
              value: (
                <Badge tone={s.tls.publicUrlHttps ? 'success' : 'warning'}>
                  {s.tls.publicUrlHttps ? 'да' : 'нет'}
                </Badge>
              ),
            },
            { label: 'Версия', value: <span className="num">{s.commit.slice(0, 7)}</span> },
          ]}
        />
      </Panel>

      {s.platform ? (
        <>
          <Panel
            title="Копии, восстановление, мониторинг"
            meta="Только владельцу портала. Отчёты присылают скрипты на серверах (infra/kz)"
          >
            <CheckList
              checks={s.platform.checks.filter(
                (c) => !['smtp', 'telegram', 'telegram_webhook'].includes(c.key),
              )}
            />
            <p className="mt-3 text-[13px] text-ink-2">
              Очередь отправки: ждут {s.platform.outbox.pending}, не доставлено за 7 дней{' '}
              {s.platform.outbox.dead}.
              {s.platform.expected.length ? ` Обязательные проверки: ${s.platform.expected.length}.` : ''}
            </p>
          </Panel>
          <Panel title="События" meta="Сбои, восстановление, отчёты о копиях">
            {s.platform.events.length ? (
              <ul className="flex flex-col divide-y divide-line text-[14px]">
                {s.platform.events.map((e) => (
                  <li key={e.id} className="flex flex-col gap-0.5 py-2 first:pt-0 last:pb-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="num text-ink-2">{dateTime(e.at)}</span>
                      <Badge
                        tone={
                          e.severity === 'error' ? 'danger' : e.severity === 'warn' ? 'warning' : 'neutral'
                        }
                      >
                        {e.severity === 'error' ? 'сбой' : e.severity === 'warn' ? 'внимание' : 'событие'}
                      </Badge>
                      <span className="font-medium text-ink">{e.title}</span>
                    </div>
                    {e.details ? (
                      <span className="text-[13px] [overflow-wrap:anywhere] text-ink-2">{e.details}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[14px] text-ink-2">Событий пока не было.</p>
            )}
          </Panel>
        </>
      ) : null}
    </div>
  );
}
